import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PAUSE_POLLING_WHEN_UNFOCUSED } from 'src/constants/polling.constants';
import { PortalDsAlert, PortalDsButton, PortalDsCard, PortalDsEmptyState, PortalDsNoticeBar, PortalDsSectionHeader, portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import { Skeleton } from 'src/components/ui/skeleton';
import {
  useGetSurveySegmentsQuery,
  useStartSegmentRunMutation,
} from 'src/redux/survey/focus-group.endpoints';
import type { CombinedQuestionStats } from 'src/pages/view-survey/view-survey.types';
import type { SurveySegment } from 'src/types/focus-group.types';
import { FocusGroupChat } from './focus-group-chat';
import { SegmentCard } from './segment-card';
import {
  buildCoverageCautionMessage,
  buildOverlapCautionMessage,
  formatCoveragePct,
  hasHighOverlap,
  hasLowCoverage,
  MIN_SURVEY_AI_RESPONDENTS,
} from './focus-group-utils';

interface FocusGroupSectionProps {
  extSurveyId: string;
  totalAiRespondents: number;
  questions: CombinedQuestionStats[];
}

export function FocusGroupSection({
  extSurveyId,
  totalAiRespondents,
  questions,
}: FocusGroupSectionProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [runError, setRunError] = useState<string | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [activeSegment, setActiveSegment] = useState<SurveySegment | null>(null);
  const [pollSegments, setPollSegments] = useState(false);

  const focusGroupSegmentId = searchParams.get('focusGroup');

  const {
    data,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useGetSurveySegmentsQuery(
    { ext_survey_id: extSurveyId },
    pollSegments
      ? { pollingInterval: 3000, ...PAUSE_POLLING_WHEN_UNFOCUSED }
      : {}
  );

  const [startSegmentRun, { isLoading: isStartingRun }] = useStartSegmentRunMutation();

  useEffect(() => {
    setPollSegments(data?.run?.run_status === 'running');
  }, [data?.run?.run_status]);

  const run = data?.run ?? null;
  const segments = data?.segments ?? [];
  const isRunInFlight = isStartingRun
    ? run?.run_status !== 'complete' && run?.run_status !== 'failed'
    : run?.run_status === 'running';
  const hasSegments = segments.length > 0;
  const droppedReasons = run?.dropped ?? [];
  const completedWithNoSegments = run?.run_status === 'complete' && !hasSegments;
  const hasEnoughRespondents = totalAiRespondents >= MIN_SURVEY_AI_RESPONDENTS;
  const showEmptyPrompt = !isLoading && !isError && !hasSegments && !isRunInFlight
    && run?.run_status !== 'complete'
    && run?.run_status !== 'failed'
    && hasEnoughRespondents;
  const showTooFewRespondents = !isLoading && !isError && !hasSegments && !isRunInFlight
    && run?.run_status !== 'complete'
    && run?.run_status !== 'failed'
    && !hasEnoughRespondents;

  const handleFindSegments = useCallback(async() => {
    setRunError(null);
    try {
      setPollSegments(true);
      await startSegmentRun({ ext_survey_id: extSurveyId }).unwrap();
      await refetch();
    } catch {
      setRunError('Could not start segment discovery. Please try again.');
    }
  }, [extSurveyId, refetch, startSegmentRun]);

  const openSegmentChat = useCallback((segment: SurveySegment) => {
    setActiveSegment(segment);
    setChatOpen(true);
    const next = new URLSearchParams(searchParams);
    next.set('focusGroup', segment.id);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleChatOpenChange = useCallback((open: boolean) => {
    setChatOpen(open);
    if (!open) {
      setActiveSegment(null);
      const next = new URLSearchParams(searchParams);
      next.delete('focusGroup');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!focusGroupSegmentId || !hasSegments) {
      return;
    }
    const segment = segments.find((item) => item.id === focusGroupSegmentId);
    if (!segment) {
      return;
    }
    setActiveSegment(segment);
    setChatOpen(true);
  }, [focusGroupSegmentId, hasSegments, segments]);

  const showCoverageCaution = run?.run_status === 'complete'
    && run.coverage_pct != null
    && hasLowCoverage(run.coverage_pct);
  const showOverlapCaution = run?.run_status === 'complete'
    && run.overlap
    && hasHighOverlap(run.overlap);

  return (
    <section
      id="overview-focus-group"
      className="px-4 py-2 md:px-6"
      data-testid="focus-group-section"
    >
      <PortalDsCard>
        <PortalDsSectionHeader
          title="Focus group"
          description="Interview simulated segment personas about this survey. Segments group AI respondents with similar answer patterns — not real individuals."
          actions={!hasSegments && !isRunInFlight && hasEnoughRespondents ? (
            <PortalDsButton
              variant="accent"
              onClick={() => { void handleFindSegments(); }}
              disabled={isStartingRun}
            >
              Find segments
            </PortalDsButton>
          ) : undefined}
        />

        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2">
            <Skeleton className="h-72 w-full rounded-xl bg-[#EDEEF2]" />
            <Skeleton className="h-72 w-full rounded-xl bg-[#EDEEF2]" />
          </div>
        ) : null}

        {isError ? (
          <PortalDsAlert
            variant="danger"
            title="Could not load focus group segments"
          >
            <PortalDsButton
              className="mt-3"
              variant="secondary"
              size="sm"
              onClick={() => { void refetch(); }}
            >
              Retry
            </PortalDsButton>
          </PortalDsAlert>
        ) : null}

        {!isLoading && !isError && showEmptyPrompt ? (
          <PortalDsEmptyState
            title="No segments yet"
            description="Segments are AI-derived groups of respondents who answered similarly. Run segment discovery to generate personas you can interview about this survey's subject matter."
            action={(
              <div className="space-y-3">
                {runError ? (
                  <p className="text-[13px] text-[#DC2626]">
                    {runError}
                  </p>
                ) : null}
                <PortalDsButton
                  variant="accent"
                  onClick={() => { void handleFindSegments(); }}
                >
                  Find segments
                </PortalDsButton>
              </div>
            )}
          />
        ) : null}

        {!isLoading && !isError && showTooFewRespondents ? (
          <PortalDsEmptyState
            title="Not enough AI respondents yet"
            description={`Focus groups need at least ${MIN_SURVEY_AI_RESPONDENTS} AI respondents so segments are large enough to be meaningful. This survey currently has ${totalAiRespondents}.`}
          />
        ) : null}

        {isRunInFlight ? (
          <div className={cn(portalDs.alertBase, portalDs.alertInfo)}>
            <div>
              <p className={portalDs.alertTitle}>
                Finding segments… this can take a minute.
              </p>
              {isFetching ? (
                <p className={cn(portalDs.alertBody, 'mt-1')}>
                  Checking progress…
                </p>
              ) : null}
            </div>
          </div>
        ) : null}

        {completedWithNoSegments ? (
          <div className="mb-4">
            <PortalDsAlert
              variant="warning"
              title="No segments met the discovery bar"
            >
              <p>
                Proposals were generated, but none had both a valid membership rule and enough matching AI respondents.
              </p>
              {droppedReasons.length > 0 ? (
                <ul className="mt-2 list-disc space-y-1 pl-5">
                  {droppedReasons.map((item, index) => (
                    <li key={`${item.name ?? 'dropped'}-${index}`}>
                      {item.name ? `${item.name}: ` : ''}
                      {item.reason}
                    </li>
                  ))}
                </ul>
              ) : null}
              {hasEnoughRespondents ? (
                <PortalDsButton
                  className="mt-3"
                  variant="secondary"
                  size="sm"
                  onClick={() => { void handleFindSegments(); }}
                >
                  Retry
                </PortalDsButton>
              ) : (
                <p className="mt-3">
                  {`Focus groups need at least ${MIN_SURVEY_AI_RESPONDENTS} AI respondents. This survey currently has ${totalAiRespondents}.`}
                </p>
              )}
            </PortalDsAlert>
          </div>
        ) : null}

        {run?.run_status === 'failed' ? (
          <div className="mb-4">
            <PortalDsAlert
              variant="danger"
              title="Segment discovery failed"
            >
              <p>
                {run.dropped?.[run.dropped.length - 1]?.reason ?? 'Please try again.'}
              </p>
              {hasEnoughRespondents ? (
                <PortalDsButton
                  className="mt-3"
                  variant="secondary"
                  size="sm"
                  onClick={() => { void handleFindSegments(); }}
                >
                  Retry
                </PortalDsButton>
              ) : (
                <p className="mt-3">
                  {`Focus groups need at least ${MIN_SURVEY_AI_RESPONDENTS} AI respondents. This survey currently has ${totalAiRespondents}.`}
                </p>
              )}
            </PortalDsAlert>
          </div>
        ) : null}

        {hasSegments ? (
          <>
            {run?.run_status === 'complete' && run.coverage_pct != null ? (
              <p className={cn(portalDs.bodySm, 'mb-3')}>
                Segments cover
                {' '}
                <span className="font-semibold text-[#0D0F1A]">
                  {formatCoveragePct(run.coverage_pct)}
                </span>
                {' '}
                of AI respondents.
              </p>
            ) : null}

            {showCoverageCaution ? (
              <div
                className="mb-3"
                data-testid="coverage-caution"
              >
                <PortalDsNoticeBar>
                  {buildCoverageCautionMessage(run?.coverage_pct ?? 0)}
                </PortalDsNoticeBar>
              </div>
            ) : null}

            {showOverlapCaution ? (
              <PortalDsNoticeBar className="mb-3">
                {buildOverlapCautionMessage()}
              </PortalDsNoticeBar>
            ) : null}

            <div className="grid gap-4 md:grid-cols-2">
              {segments.map((segment) => (
                <SegmentCard
                  key={segment.id}
                  segment={segment}
                  totalAiRespondents={totalAiRespondents}
                  questions={questions}
                  onOpenChat={() => openSegmentChat(segment)}
                />
              ))}
            </div>
          </>
        ) : null}
      </PortalDsCard>

      <FocusGroupChat
        open={chatOpen}
        onOpenChange={handleChatOpenChange}
        segment={activeSegment}
        totalAiRespondents={totalAiRespondents}
        questions={questions}
        searchParams={searchParams}
      />
    </section>
  );
}
